/**
 * The frozen formatter set. A template can only present a value in one of these
 * ways; anything else would be presentation logic escaping into the package.
 *
 * Behaviour is deliberately identical to the helpers the TSX templates use
 * (`golden-serenity/utils.ts` formatDate, `eventUtils.normalizeMultilineText`,
 * `getCountryDisplayName`) so the two render the same words.
 */

import { getCountryDisplayName, normalizeMultilineText } from "@/utils/eventUtils";
import { formatEventDate } from "@/pages/public/templates/shared/dateTime";

/**
 * Separator the host uses to hand multi-part names to `text.initials`.
 * A control character, because real names contain spaces, hyphens and ampersands.
 */
export const NAME_PART_SEPARATOR = "\u0000";

// JUB-127: delegates to the shared, timezone-safe formatter so the package
// keeps rendering "the same words" as the TSX golden-serenity template
// (see docstring above) instead of shifting into the viewer's browser timezone.
function intlDate(value: string, locale: string, options: Intl.DateTimeFormatOptions): string {
  return formatEventDate(value, locale, options);
}

const DATE_OPTIONS: Record<string, Intl.DateTimeFormatOptions> = {
  "date.long": { day: "numeric", month: "long", year: "numeric" },
  "date.full": { weekday: "long", day: "numeric", month: "long", year: "numeric" },
  "date.weekdayLong": { weekday: "long" },
  "date.dayNumber": { day: "numeric" },
  "date.monthShort": { month: "short" },
  "date.monthLong": { month: "long" },
  "date.year": { year: "numeric" },
  "date.time": { hour: "2-digit", minute: "2-digit" },
};

export type FormatterOptions = { locale: string; sep?: string };

export function applyFormatter(name: string, raw: unknown, options: FormatterOptions): string {
  if (raw === null || raw === undefined || raw === "") return "";

  if (name in DATE_OPTIONS) {
    return intlDate(String(raw), options.locale, DATE_OPTIONS[name]);
  }

  switch (name) {
    case "text.multiline":
      return normalizeMultilineText(String(raw));

    case "text.initials": {
      const separator = options.sep ?? " & ";
      return String(raw)
        .split(NAME_PART_SEPARATOR)
        .map((part) => part.trim().charAt(0))
        .filter(Boolean)
        .join(separator);
    }

    case "text.hashtag": {
      const tag = String(raw).trim();
      return tag.startsWith("#") ? tag : `#${tag}`;
    }

    case "number.pad2":
      return String(raw).padStart(2, "0");

    case "country.display":
      return getCountryDisplayName(String(raw), options.locale) ?? "";

    default:
      throw new Error(`Unknown formatter: ${name}`);
  }
}

export const FORMATTER_NAMES = [
  ...Object.keys(DATE_OPTIONS),
  "text.multiline",
  "text.initials",
  "text.hashtag",
  "number.pad2",
  "country.display",
];
